import React, { useMemo, useEffect, useState } from 'react';
import { Typography, Table, Dropdown, Button, Tag, Flex } from 'antd';
import { MoreOutlined, DeleteOutlined } from '@ant-design/icons';

import { PageTitle } from 'components';
import { useChangePageTitle } from 'utils/useChangePageTitle';
import { Link } from 'react-router-dom';

import useUser from 'hooks/useUser';
import useTeamStandings from 'hooks/useTeamStandings';
import useMiniLeagues from 'hooks/useMiniLeagues';
import useScreenSize from 'hooks/useScreenSize';

import JoinLeagueModal from 'pages/Leagues/JoinLeagueModal';
import CreateLeagueModal from 'pages/Leagues/CreateLeagueModal';
import DeleteLeagueModal from 'pages/Leagues/DeleteLeagueModal';
import PremiumWrapper from 'wrappers/PremiumWrapper';
import LoggedInGuard from 'wrappers/LoggedInGuard';

const { Title, Paragraph } = Typography;

const overallColumns = [
  { title: 'Rank', dataIndex: 'rank', key: 'rank' },
  { title: 'Team Name', dataIndex: 'name', key: 'name' },
  { title: 'Total Points', dataIndex: 'totalPoints', key: 'totalPoints' },
];

const miniLeagueColumns = (userId, onDelete) => [
  {
    title: 'Rank',
    dataIndex: 'rank',
    key: 'rank',
  },
  {
    title: 'League',
    dataIndex: 'name',
    key: 'name',
    render: (name, record) => <Flex align="center" gap="small">
      <Link to={`/team-standings/${record.id}`}>{name}</Link>
      {record.createdBy === userId && <Tag>Owner</Tag>}
    </Flex>
  },
  {
    key: 'actions',
    render: (_, record) => record.createdBy === userId
      ? <Flex align="center" justify="end" gap="small">
          <Paragraph style={{ marginBottom: 0 }} code copyable>{record.joinCode}</Paragraph>
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} />
        </Flex>
      : null
  }
];

const Leagues = () => {

  const changePageTitle = useChangePageTitle();

  const { userData } = useUser();
  const [{ isMobile }] = useScreenSize();
  const { teamStandings, isLoading: standingsLoading } = useTeamStandings('current');
  const { miniLeagues, isLoading: miniLeaguesLoading, mutateMiniLeagues } = useMiniLeagues();

  const myOverallRow = useMemo(() => {
    if (!teamStandings || !userData?.teamId) return [];

    const myIndex = teamStandings.findIndex(team => team.id === userData.teamId);
    if (myIndex === -1) return [];

    return [{ ...teamStandings[myIndex], rank: myIndex + 1 }];
  }, [teamStandings, userData]);

  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [leagueToDelete, setLeagueToDelete] = useState(null);

  useEffect(() => {
    changePageTitle('Leagues');
  }, []);

  const menuItems = [
    {
      key: 'create-league',
      label: "Create League",
      onClick: () => setCreateModalOpen(true)
    },
    {
      key: 'join-league',
      label: "Join League",
      onClick: () => setJoinModalOpen(true)
    }
  ];

  function ActionsMenu() {
    return (
      <Dropdown
        placement="bottomRight"
        trigger="click"
        menu={{ items: menuItems }}
      >
        <Button shape="circle" icon={<MoreOutlined />} />
      </Dropdown>
    );
  }

  function ActionButtons() {
    return (
      <Flex gap="small">
        <Button onClick={() => setCreateModalOpen(true)}>Create League</Button>
        <Button onClick={() => setJoinModalOpen(true)}>Join League</Button>
      </Flex>
    );
  }

  return (
    <LoggedInGuard>
      <PremiumWrapper>
        <JoinLeagueModal open={joinModalOpen} onClose={() => setJoinModalOpen(false)} />
        <CreateLeagueModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onCreated={mutateMiniLeagues} 
          />
        <DeleteLeagueModal 
          open={!!leagueToDelete}
          league={leagueToDelete}
          onClose={() => setLeagueToDelete(null)}
          onDeleted={mutateMiniLeagues} 
          />

        <PageTitle title="Leagues" extra={[isMobile ? <ActionsMenu key="actions" /> : <ActionButtons key="actions" />]} />
        <Title level={4}>Overall</Title>
        <Table
          columns={overallColumns}
          dataSource={myOverallRow}
          rowKey="id"
          loading={standingsLoading}
          pagination={false}
          size="small"
        />
        <Title level={4} style={{ marginTop: 24 }}>Mini Leagues</Title>
        <Table
          columns={miniLeagueColumns(userData?.id, setLeagueToDelete)}
          dataSource={miniLeagues ?? []}
          rowKey="id"
          loading={miniLeaguesLoading}
          pagination={false}
          size="small"
        />
      </PremiumWrapper>
    </LoggedInGuard>
  );
};

export default Leagues;
